import * as React from "react";
import { useState, useEffect } from "react";
import { useHistory} from 'react-router';
import SiteMap from "../../Commons/SiteMap";
import SessionManager from "../SessionManager";
import { getDesign } from "../../Commons/WhiteLabeling";

import { PopUpError } from "../../Commons/Helpers";
import { useLocalContent } from "../GetLanguageAuth/useLocalContent";
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Spinner from "react-bootstrap/Spinner";
import Link from "../../Commons/SiteMap/Link";
import AuthLanguageForm from "../GetLanguageAuth"

// Enhanced error response interfaces
interface LoginErrorResponse {
    error?: string;
    remaining_attempts?: number;
    minutes_remaining?: number;
    seconds_remaining?: number;
}

// Error handling functions
const formatLockoutTime = (minutes: number | undefined, seconds: number | undefined, getContent: (key: string) => string): string => {
    if (minutes === undefined && seconds === undefined) return '';

    if (minutes && minutes > 0 && seconds && seconds > 0) {
        return getContent('login__lockout_time_mixed')
            .replace('{minutes}', minutes.toString())
            .replace('{seconds}', seconds.toString());
    } else if (minutes && minutes > 0) {
        return getContent('login__lockout_time_minutes')
            .replace('{minutes}', minutes.toString());
    } else if (seconds && seconds > 0) {
        return getContent('login__lockout_time_seconds')
            .replace('{seconds}', seconds.toString());
    }

    return '';
};

const handleLoginError = (error: any, getContent: (key: string) => string): void => {
    // Try to parse structured error response
    let errorData: LoginErrorResponse = {};

    try {
        // Check if error is from jQuery XHR response
        if (error && error.responseJSON) {
            errorData = error.responseJSON;
        } else if (error && typeof error === 'object') {
            errorData = error;
        }
    } catch (e) {
        console.warn('Failed to parse error response:', e);
    }

    // Handle different error types
    console.log('errorData', errorData);
    if (errorData.error === 'invalid_credentials_remaining_attempts' && errorData.remaining_attempts !== undefined) {
        const message = getContent('login__remaining_attempts')
            .replace('{attempts}', errorData.remaining_attempts.toString());
        PopUpError(message);
    } else if (errorData.error === 'account_locked') {
        const timeMessage = formatLockoutTime(errorData.minutes_remaining, errorData.seconds_remaining, getContent);
        const baseMessage = getContent('login__account_locked');
        const fullMessage = timeMessage ? `${baseMessage} ${timeMessage}` : baseMessage;
        PopUpError(fullMessage);
    } else {
        // Fallback to generic error message
        PopUpError(getContent('login__authentication__failed'));
    }
};

const Login: React.FC = () => {

    let history = useHistory();
    let getContent = useLocalContent();

    let [formValue, setFormValue] = useState({email:'', password:''});
    let [checkingLogin, setCheckingLogin] = useState<boolean>(false);
    let [errorFormClass, setErrorFormClass ] = useState<boolean>(false);

    const mobiles = require('is-mobile');
    let mobile = mobiles();

    // Initially check if loggin info is set
    useEffect(() => {
        if (localStorage.getItem('jwt_token') !== null) {
            history.push(SiteMap.getFullURL('dashboard'));
        }
    });

    const handleInput = (event: any) =>{
        const {name, value} = event.target;
        setFormValue({...formValue, [name]: value});
    }

    const handleSubmit = (event : React.FormEvent<HTMLFormElement>)=>{
        event.preventDefault();
        setCheckingLogin(true);
        SessionManager.login(
            formValue.email, formValue.password,
            () => {
                setCheckingLogin(false);
                setErrorFormClass(false);
            },
            (error) => {
                setFormValue({email:'', password:''});
                handleLoginError(error, getContent);
                setCheckingLogin(false);
                setErrorFormClass(true);
            }
        );
    }

    const whiteLabels = getDesign();

    return (
        <Container fluid className={'deg-bg vh-100 pt-5'}>
            <Row className={'text-center d-flex align-items-center pt-5'}>
                <Col sm={5} className={'d-none d-lg-block pl-0'}>
                    <img
                        src={whiteLabels.logos.login}
                        alt={'logo'}
                        className={'w-75 align-self-center'}
                    style={{marginTop: '-80px'}}
                    />
                </Col>
                <Col sm={12} lg={7}>
                <Card className={'bg-light m-auto p-3 mt-4 ' + (mobile ? 'w-100': 'w-50')}>
                    <Card.Body>
                        <Card.Title className={'d-none d-lg-block  m-auto'}>
                            <p>{getContent('login__authentication__title')}</p>
                        </Card.Title>
                        <Card.Text as='div'>
                            <Form className={'text-left'} onSubmit={handleSubmit}>
                                <Form.Group >
                                    <Form.Control
                                        as={'input'}
                                        name={'email'}
                                        type={'email'}
                                        value={formValue.email}
                                        onChange={handleInput}
                                        placeholder={getContent('login__authentication__placeholder_email')}
                                        required
                                        isInvalid={errorFormClass}
                                    />
                                </Form.Group>
                                <Form.Group >
                                    <Form.Control
                                        as={'input'}
                                        type={'password'}
                                        name={'password'}
                                        value={formValue.password}
                                        onChange={handleInput}
                                        placeholder={getContent('login__authentication__placeholder_password')}
                                        required
                                        isInvalid={errorFormClass}
                                    />
                                    <Form.Control.Feedback type="invalid" className={'text-center'}>
                                        {getContent('login__authentication__invalid_inputs')}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Button type={'submit'} disabled={checkingLogin} className={'w-100'}>
                                    {!checkingLogin && <p className={'p-0 mb-0'}>{getContent('login__authentication__button')}</p>}
                                    {checkingLogin && <Spinner animation="border" variant="light" />}
                                </Button>
                            </Form>
                        </Card.Text>
                            <Link to={'reset_password'} className="mt-2">
                                {getContent('login__authentication__reset_password_button')}
                            </Link>
                    </Card.Body>
                    </Card>
                    <AuthLanguageForm />
                </Col>
            </Row>
        </Container>
    );
};

export default Login;
